import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { get, post } from '@utils/axios_api';
import { getStoredUserId } from '@utils/authStorage';

// Express API 필드키 → 폼 필드키 매핑
const API_TO_FORM_KEY = {
    person_name:  'name',
    company_name: 'company',
    job_title:    'title',
    phone:        'phone',
    email:        'email',
    address:      'address',
    website:      'website',
};

const FORM_TO_API_KEY = Object.fromEntries(
    Object.entries(API_TO_FORM_KEY).map(([apiKey, formKey]) => [formKey, apiKey])
);

export const FIELD_LABELS = {
    name:       '이름',
    company:    '회사명',
    department: '부서',
    title:      '직함',
    mobile:     '휴대폰',
    phone:      '전화',
    fax:        '팩스',
    email:      '이메일',
    address:    '주소',
    website:    '웹사이트',
};

export const BIZ_FIELD_KEYS = Object.keys(FIELD_LABELS);

const fetchReview  = (cardId) => get(`/bizscanpro/cards/${cardId}/review`);
const runReanalyze = (cardId) => post(`/bizscanpro/cards/${cardId}/analyze`, { use_llm: true });

// card.ocr_raw_json.card_detect → crop overlay 데이터
function buildCropOverlay(cardDetect) {
    const points = Array.isArray(cardDetect?.points) ? cardDetect.points : [];
    if (points.length !== 4) return null;
    return {
        points,
        method: cardDetect?.method || '-',
        found:  Boolean(cardDetect?.found),
        width:  cardDetect?.width  || 0,
        height: cardDetect?.height || 0,
    };
}

// OCR 탭에 표시할 필드 배열
function buildOcrFields(card) {
    const ocr = card?.ocr_raw_json;
    if (!ocr) return [];
    return [
        {
            key:        'ocr_full_text',
            label:      'OCR 전체 텍스트',
            value:      ocr.full_text || '-',
            confidence: ocr.average_confidence ?? null,
            multiline:  true,
            minHeight:  160,
        },
        {
            key:        'ocr_accuracy',
            label:      'OCR 정확도',
            value:      ocr.average_confidence != null
                            ? `${Math.round(Number(ocr.average_confidence) * 100)}%`
                            : '-',
            confidence: null,
        },
        {
            key:        'ocr_engine',
            label:      'OCR 엔진',
            value:      ocr.engine || '-',
            confidence: null,
        },
        {
            key:        'ocr_token_count',
            label:      '토큰 수',
            value:      String(ocr.token_count ?? 0),
            confidence: null,
        },
        {
            key:        'llm_request_mode',
            label:      'LLM 요청 방식',
            value:      card?.llm_request_mode || '-',
            confidence: null,
        },
    ];
}

// LLM 디버그 정보
function buildDebugInfo(card) {
    if (!card) return null;
    return {
        llmEnabledForRequest: card.llm_enabled_for_request,
        llmCalled:            card.llm_called,
        llmCallSuccess:       card.llm_call_success,
        llmRequestMode:       card.llm_request_mode,
        llmProvider:          card.llm_provider,
        llmModelName:         card.llm_model_name,
        llmErrorMessage:      card.llm_error_message,
    };
}

export function useBizCardReview(cardId) {
    const navigate    = useNavigate();
    const queryClient = useQueryClient();
    const [isConfirming,  setIsConfirming]  = useState(false);
    const [isReanalyzing, setIsReanalyzing] = useState(false);
    const [activeTab,     setActiveTab]     = useState('llm');

    const { data: reviewRaw, isLoading, isError, refetch } = useQuery({
        queryKey:  ['bizCardReview', cardId],
        queryFn:   () => fetchReview(cardId),
        enabled:   !!cardId,
        staleTime: 0,
    });

    const methods = useForm({
        defaultValues:    Object.fromEntries(BIZ_FIELD_KEYS.map((k) => [k, ''])),
        shouldFocusError: false,
    });

    // Express review 응답: { ok, message, card: { ...serializeBizscanCard } }
    const card = reviewRaw?.card ?? null;

    // LLM/Admin 필드 → 폼 초기값 세팅
    useEffect(() => {
        if (!card) return;
        const parseJson = (raw) => {
            if (!raw) return null;
            if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return null; } }
            return raw;
        };
        const finalJson  = parseJson(card.admin_final_json);
        const llmJson    = parseJson(card.llm_structured_json);
        const apiFields  = finalJson?.fields ?? llmJson?.fields ?? [];
        const formValues = Object.fromEntries(BIZ_FIELD_KEYS.map((k) => [k, '']));
        for (const f of apiFields) {
            const formKey = API_TO_FORM_KEY[f.key] ?? f.key;
            if (formKey in formValues) formValues[formKey] = f.value ?? '';
        }
        methods.reset(formValues);
    }, [card]); // eslint-disable-line react-hooks/exhaustive-deps

    // 화면에서 사용할 정규화 데이터
    const data = useMemo(() => {
        if (!card) return null;
        return {
            review_status: card.status === 'completed' ? 'confirmed'
                         : card.status === 'ocr_failed' ? 'error'
                         : 'pending',
            ocr: {
                text:           card.ocr_raw_json?.full_text ?? null,
                avg_confidence: card.ocr_average_confidence ?? card.ocr_raw_json?.average_confidence ?? null,
            },
            llm: {
                model: card.llm_model_name ?? null,
            },
            reg_id:       card.confirmed_by ?? null,
            reg_dt:       card.created_at   ?? null,
            confirmed_at: card.confirmed_at ?? null,
        };
    }, [card]);

    // 이미지 URL: image_url = '/bizscanpro/storage/original/xxx.jpg'
    // Spring Boot 프록시: GET /api/bizscanpro/storage/** → Express
    const imageUrl = useMemo(() => {
        const imgPath = card?.image_url ?? null;
        if (!imgPath) return null;
        return `${process.env.REACT_APP_BOOT_URL}/api${imgPath}`;
    }, [card]);

    // crop overlay — card.ocr_raw_json.card_detect에서 직접 추출 (detect-preview 재호출 불필요)
    const cropOverlay = useMemo(() => buildCropOverlay(card?.ocr_raw_json?.card_detect), [card]);

    // OCR 탭 필드 배열
    const ocrFields = useMemo(() => buildOcrFields(card), [card]);

    // LLM 디버그 정보
    const debugInfo = useMemo(() => buildDebugInfo(card), [card]);

    const handleReanalyze = async () => {
        if (!window.confirm('LLM을 다시 실행합니다. 현재 입력값이 초기화됩니다. 계속하시겠습니까?')) return;
        try {
            setIsReanalyzing(true);
            await runReanalyze(cardId);
            await refetch();
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || 'LLM 재실행 중 오류가 발생했습니다.';
            toast.error(msg);
        } finally {
            setIsReanalyzing(false);
        }
    };

    const handleConfirm = async (formValues) => {
        if (!window.confirm('검수 내용을 확정하시겠습니까?')) return;
        try {
            setIsConfirming(true);
            const fields = BIZ_FIELD_KEYS
                .filter((k) => formValues[k])
                .map((k) => ({
                    key:          FORM_TO_API_KEY[k] ?? k,
                    label:        FIELD_LABELS[k],
                    value:        formValues[k],
                    confidence:   1.0,
                    alternatives: [],
                }));

            await post(`/bizscanpro/cards/${cardId}/confirm`, {
                reviewer: getStoredUserId() || 'admin',
                fields,
            });
            queryClient.removeQueries({ queryKey: ['bizCardList'] });
            queryClient.invalidateQueries({ queryKey: ['bizCardDetail', Number(cardId)] });
            toast.success('검수가 확정되었습니다.');
            navigate('/biz/card-list');
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || '검수 확정 중 오류가 발생했습니다.';
            toast.error(msg);
        } finally {
            setIsConfirming(false);
        }
    };

    return {
        methods,
        data,
        isLoading,
        isError,
        imageUrl,
        isConfirming,
        isReanalyzing,
        handleConfirm: methods.handleSubmit(handleConfirm),
        handleReanalyze,
        cropOverlay,
        ocrFields,
        debugInfo,
        activeTab,
        setActiveTab,
    };
}
